import { entityTypePath } from '@utils/settings';
import { investmentEntity } from '@store/modules/investment-entity';
import { redirect } from '@src/utils/client';
import { RouteConfig } from 'vue-router';
import { user } from '@store/modules/user';

/* eslint-disable @typescript-eslint/explicit-function-return-type */
const settingsRoutes: Array<RouteConfig> = [
	{
		path: `/account/email/form`,
		name: 'settings-email-form',
		component: () => import(/* webpackChunkName: "settings-email-form" */ '@views/settings/settings-email-form.vue')
	},
	{
		path: `/account/notifications/view`,
		name: 'settings-notifications-view',
		component: () =>
			import(
				/* webpackChunkName: "settings-notifications-view" */ '@views/settings/settings-notifications-view.vue'
			)
	},
	{
		path: `/account/notifications/edit`,
		name: 'settings-notifications-edit',
		component: () =>
			import(
				/* webpackChunkName: "settings-notifications-edit" */ '@views/settings/settings-notifications-edit.vue'
			)
	},
	{
		path: `/account/email/view`,
		name: 'settings-email-view',
		component: () => import(/* webpackChunkName: "settings-email-view" */ '@views/settings/settings-email-view.vue')
	},
	{
		path: `/account/investment-accounts/joint-account/personal-information`,
		alias: [`/account/investment-accounts/:investmentEntityId/joint-account/personal-information`],
		name: 'settings-joint-account-information-view',
		beforeEnter: async (to, from, next) => {
			await investmentEntity.storeJointAccountDetails();
			next();
		},
		component: () =>
			import(
				/* webpackChunkName: "settings-joint-account-information-view" */ '@src/views/settings/settings-joint-account-information-view.vue'
			)
	},
	{
		path: `/account/investment-accounts/:investmentEntityId/joint-account/account-holder-information/edit`,
		alias: [`/account/investment-accounts/joint-account/account-holder-information/edit`],
		name: 'settings-joint-account-information-edit',
		beforeEnter: async (to, from, next) => {
			await investmentEntity.storeJointAccountDetails();
			next();
		},
		component: () =>
			import(
				/* webpackChunkName: "settings-joint-account-information-edit" */ '@views/settings/settings-joint-account-information-edit.vue'
			)
	},
	{
		path: '/account/investment-accounts/new/joint-account/create',
		name: 'settings-joint-account-new',
		beforeEnter: async (to, from, next) => {
			await user.storeAddress();
			next();
		},
		component: () =>
			import(
				/* webpackChunkName: "settings-joint-account-information-edit" */ '@views/settings/settings-joint-account-information-edit.vue'
			)
	},
	{
		path: `/account/investment-accounts/:investmentEntityId/:entityType/recurring-investment-settings/manage`,
		beforeEnter: async (to, from, next) => {
			if (investmentEntity.eligibleForRecurringInvestment) {
				await investmentEntity.storeAutoInvestSchedule();
				await investmentEntity.storeBankAccounts();
				next();
			} else {
				redirect('/account/overview');
			}
		},
		name: 'settings-auto-invest-form',
		component: () =>
			import(/* webpackChunkName: "settings-auto-invest-form" */ '@views/settings/settings-auto-invest-form.vue')
	},
	{
		path: `/account/investment-accounts/:investmentEntityId/:entityType/recurring-investment-settings/view`,
		name: 'auto-invest-view',
		beforeEnter: async (to, from, next) => {
			if (investmentEntity.eligibleForRecurringInvestment) {
				await investmentEntity.storeAutoInvestSchedule();
				await investmentEntity.storeBankAccounts();
				next();
			} else {
				redirect('/account/overview');
			}
		},
		component: () =>
			import(/* webpackChunkName: "settings-auto-invest-view" */ '@views/settings/settings-auto-invest-view.vue')
	},
	{
		path: `/account/investment-accounts/:investmentEntityId/:entityType/dividend-reinvestment/view`,
		alias: [
			`/account/investment-accounts/:investmentEntityId/:entityType/ria-dividend-reinvestment/view`,
			'/account/investment-accounts/ria-dividend-reinvestment/view',
			'/account/investment-accounts/dividend-reinvestment/view'
		],
		name: 'settings-drip-view',
		beforeEnter: async (to, from, next) => {
			if (investmentEntity.eligibleForDrip) {
				await investmentEntity.storeDripPreference();
				await investmentEntity.storeBankAccounts();
				next();
			} else {
				next({ name: 'ineligible-for-drip' });
			}
		},
		component: () => import(/* webpackChunkName: "settings-drip-view" */ '@views/settings/settings-drip-view.vue')
	},
	{
		path: `/account/investment-accounts/:investmentEntityId/:entityType/dividend-reinvestment/manage`,
		alias: [
			`/account/investment-accounts/:investmentEntityId/:entityType/ria-dividend-reinvestment/manage`,
			'/account/investment-accounts/ria-dividend-reinvestment/manage',
			'/account/investment-accounts/dividend-reinvestment/manage'
		],
		name: 'settings-drip-edit',
		beforeEnter: async (to, from, next) => {
			await investmentEntity.storeBankAccounts();
			if (investmentEntity.isLegacyInvestorWithCashDistribution || investmentEntity.bankAccounts.length === 0) {
				next({
					name: 'settings-drip-view',
					params: {
						investmentEntityId: investmentEntity.investmentEntityId,
						entityType: entityTypePath(investmentEntity.entityTypeLabel)
					}
				});
			} else if (investmentEntity.eligibleForDrip) {
				await investmentEntity.storeDripPreference();
				next();
			} else {
				next({ name: 'ineligible-for-drip' });
			}
		},
		component: () => import(/* webpackChunkName: "settings-drip-edit" */ '@views/settings/settings-drip-edit.vue')
	},
	{
		path: `/account/investment-accounts/new/entity-account/create`,
		name: 'settings-entity-account-new',
		component: () =>
			import(
				/* webpackChunkName: "settings-entity-account-information-edit" */ '@src/views/settings/settings-entity-account-information-edit.vue'
			)
	},
	{
		path: `/account/investment-accounts/:investmentEntityId/entity-account/entity-information/edit`,
		name: 'settings-entity-account-information-edit',
		beforeEnter: async (to, from, next) => {
			await user.storeEntityAccountDetails();
			next();
		},
		component: () =>
			import(
				/* webpackChunkName: "settings-entity-account-information-edit" */ '@src/views/settings/settings-entity-account-information-edit.vue'
			)
	},
	{
		path: `/account/investment-accounts/:investmentEntityId/entity-account/personal-information`,
		name: 'settings-entity-account-information-view',
		beforeEnter: async (to, from, next) => {
			await user.storeEntityAccountDetails();
			next();
		},
		component: () =>
			import(
				/* webpackChunkName: "settings-entity-account-information-view" */ '@src/views/settings/settings-entity-account-information-view.vue'
			)
	},
	{
		path: `/account/investment-accounts/:investmentEntityId/:entityType/investment-plan/edit`,
		alias: [`/account/investment-accounts/:investmentEntityId/:entityType/investment-goal/edit`],
		name: 'settings-investment-plan-edit',
		beforeEnter: async (to, from, next) => {
			if (investmentEntity.canUpdateInvestmentPlan) {
				next();
			} else {
				redirect('/account/overview');
			}
		},
		component: () =>
			import(
				/* webpackChunkName: "settings-investment-plan-edit" */ '@src/views/settings/settings-investment-plan-edit.vue'
			)
	},
	{
		path: `/account/investment-accounts/:investmentEntityId/:entityType/investment-plan/view`,
		alias: [`/account/investment-accounts/:investmentEntityId/:entityType/investment-goal/manage`],
		name: 'settings-investment-plan-view',
		beforeEnter: async (to, from, next) => {
			if (!investmentEntity.currentRiaPlanName) {
				redirect('/account/overview');
			} else {
				next();
			}
		},
		component: () =>
			import(
				/* webpackChunkName: "settings-investment-plan-view" */ '@views/settings/settings-investment-plan-view.vue'
			)
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/ira/manage',
		name: 'settings-ira-manage',
		beforeEnter: async (to, from, next) => {
			await investmentEntity.storeIraAccountDetails();
			next({
				name: 'settings-ira-information-view',
				params: {
					investmentEntityId: investmentEntity.investmentEntityId,
					entityType: entityTypePath(investmentEntity.entityTypeLabel)
				},
				replace: true
			});
		}
	},
	{
		path: `/account/investment-accounts/nickname/view`,
		name: 'settings-nickname-view',
		component: () =>
			import(/* webpackChunkName: "settings-nickname-view" */ '@views/settings/settings-nickname-view.vue')
	},
	{
		path: '/account/investment-accounts/nickname/edit',
		name: 'settings-nickname-form',
		component: () =>
			import(/* webpackChunkName: "settings-nickname-form" */ '@views/settings/settings-nickname-form.vue')
	},
	{
		path: '/account/security/mobile-number/edit',
		name: 'settings-mobile-number-edit',
		beforeEnter: async (to, from, next) => {
			await user.storePhoneData();
			next();
		},
		component: () =>
			import(
				/* webpackChunkName: "settings-mobile-number-edit" */ '@views/settings/settings-mobile-number-edit.vue'
			)
	},
	{
		path: `/account/security/mobile-number/view`,
		name: 'settings-mobile-number-view',
		beforeEnter: async (to, from, next) => {
			await user.storePhoneData();
			next();
		},
		component: () =>
			import(
				/* webpackChunkName: "settings-mobile-number-view" */ '@views/settings/settings-mobile-number-view.vue'
			)
	},
	{
		path: `/account/investment-accounts/:investmentEntityId/individual-account/personal-information/edit`,
		alias: [`/account/investment-accounts/individual-account/personal-information/edit`],
		name: 'settings-personal-information-edit',
		beforeEnter: async (to, from, next) => {
			await user.storeAddress();
			next();
		},
		component: () =>
			import(
				/* webpackChunkName: "settings-personal-information-edit" */ '@views/settings/settings-personal-information-edit.vue'
			)
	},
	{
		path: `/account/investment-accounts/individual-account/personal-information`,
		alias: [`/account/investment-accounts/:investmentEntityId/individual-account/personal-information`],
		name: 'settings-personal-information-view',
		beforeEnter: async (to, from, next) => {
			await user.storeAddress();
			next();
		},
		component: () =>
			import(
				/* webpackChunkName: "settings-personal-information-view" */ '@views/settings/settings-personal-information-view.vue'
			)
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/ira/personal-information',
		name: 'settings-ira-information-view',
		beforeEnter: async (to, from, next) => {
			await investmentEntity.storeIraAccountDetails();
			next();
		},
		component: () =>
			import(
				/* webpackChunkName: "settings-ira-information-view" */ '@views/settings/settings-ira-information-view.vue'
			)
	},
	{
		path: '/account/security/manage-password',
		name: 'settings-password-form',
		component: () =>
			import(/* webpackChunkName: "settings-password-form" */ '@views/settings/settings-password-form.vue')
	},
	{
		path: '/account/security/view-password',
		name: 'settings-view-password',
		component: () =>
			import(/* webpackChunkName: "settings-password-view" */ '@views/settings/settings-view-password.vue')
	},
	{
		path: `/account/security/removed-device/:deviceId`,
		alias: `/account/security/removedDevice/:deviceId`,
		name: 'settings-removed-device',
		component: () =>
			import(/* webpackChunkName: "settings-removed-device" */ '@views/settings/settings-removed-device.vue')
	},
	{
		path: '/account/investment-accounts/new/trust/create',
		name: 'settings-trust-account-new',
		component: () =>
			import(
				/* webpackChunkName: "settings-trust-account-edit" */ '@views/settings/settings-trust-information-edit.vue'
			)
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/trust/personal-information',
		name: 'settings-trust-information-view',
		beforeEnter: async (to, from, next) => {
			await user.storeTrustAccountDetails();
			next();
		},
		component: () =>
			import(
				/* webpackChunkName: "settings-trust-information-view" */ '@views/settings/settings-trust-information-view.vue'
			)
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/trust/information/edit',
		name: 'settings-trust-information-edit',
		beforeEnter: async (to, from, next) => {
			await user.storeTrustAccountDetails();
			next();
		},
		component: () =>
			import(
				/* webpackChunkName: "settings-trust-information-edit" */ '@views/settings/settings-trust-information-edit.vue'
			)
	},
	{
		path: '/account/security/your-devices/edit',
		name: 'settings-your-devices-edit',
		component: () =>
			import(
				/* webpackChunkName: "settings-your-devices-edit" */ '@views/settings/settings-your-devices-edit.vue'
			),
		beforeEnter: async (to, from, next) => {
			await user.storeDevices();
			next();
		}
	},
	{
		path: '/account/security/your-devices/manage',
		name: 'settings-your-devices-view',
		beforeEnter: async (to, from, next) => {
			await user.storeDevices();
			next();
		},
		component: () =>
			import(
				/* webpackChunkName: "settings-your-devices-view" */ '@views/settings/settings-your-devices-view.vue'
			)
	},
	{
		path: `/account/investment-accounts/:investmentEntityId/:entityType/bank-accounts/manage`,
		alias: [`/account/investment-accounts/:entityType/bank-accounts/manage`],
		name: 'settings-bank-accounts-view',
		beforeEnter: async (to, from, next) => {
			await investmentEntity.storeBankAccounts();
			next();
		},
		component: () =>
			import(
				/* webpackChunkName: "settings-bank-accounts-view" */ '@views/settings/settings-bank-accounts-view.vue'
			)
	},
	{
		path: `/account/investment-accounts/:investmentEntityId/:entityType/accreditation/edit`,
		alias: [`/account/investment-accounts/:entityType/accreditation/edit`],
		name: 'settings-accreditation-edit',
		component: () =>
			import(
				/* webpackChunkName: "settings-accreditation-edit" */ '@views/settings/settings-accreditation-edit.vue'
			)
	},
	{
		path: `/account/investment-accounts/:investmentEntityId/:entityType/bank-accounts/edit`,
		alias: [`/account/investment-accounts/:entityType/bank-accounts/edit`],
		name: 'settings-bank-accounts-edit',
		beforeEnter: async (to, from, next) => {
			await investmentEntity.storeBankAccounts();
			next();
		},
		component: () =>
			import(
				/* webpackChunkName: "settings-bank-accounts-edit" */ '@views/settings/settings-bank-accounts-edit.vue'
			)
	},
	{
		path: `/account/investment-accounts/:investmentEntityId/:entityType/bank-accounts/new/form`,
		alias: [`/account/investment-accounts/:entityType/bank-accounts/new/form`],
		name: 'settings-bank-accounts-new',
		component: () =>
			import(
				/* webpackChunkName: "settings-bank-accounts-new" */ '@views/settings/settings-bank-accounts-new.vue'
			)
	},
	{
		path: `/account/investment-accounts/:investmentEntityId/:entityType/advanced-options/manage`,
		alias: ['/account/investment-accounts/individual-account/advanced-options/manage'],
		name: 'settings-advanced-options',
		component: () =>
			import(/* webpackChunkName: "settings-advanced-options" */ '@views/settings/settings-advanced-options.vue')
	},
	{
		path: `/account/investment-accounts/:investmentEntityId/:entityType/beneficiaries/:beneficiaryId/edit`,
		name: 'settings-beneficiary-edit',
		beforeEnter: async (to, from, next) => {
			await investmentEntity.storeBeneficiaries();
			next();
		},
		component: () =>
			import(/* webpackChunkName: "settings-beneficiary-form" */ '@views/settings/settings-beneficiary-form.vue')
	},
	{
		path: `/account/investment-accounts/:investmentEntityId/:entityType/beneficiaries/view`,
		alias: [`/account/investment-accounts/:investmentEntityId/:entityType/beneficiaries/manage`],
		name: 'settings-beneficiaries-view',
		beforeEnter: async (to, from, next) => {
			await investmentEntity.storeBeneficiaries();
			next();
		},
		component: () =>
			import(
				/* webpackChunkName: "settings-beneficiaries-view" */ '@views/settings/settings-beneficiaries-view.vue'
			)
	},
	{
		path: `/account/investment-accounts/:investmentEntityId/:entityType/beneficiaries/new/:beneficiaryType`,
		name: 'settings-beneficiary-new',
		beforeEnter: async (to, from, next) => {
			await investmentEntity.storeBeneficiaries();
			next();
		},
		component: () =>
			import(/* webpackChunkName: "settings-beneficiary-form" */ '@views/settings/settings-beneficiary-form.vue')
	},
	{
		path: `/account/investment-accounts/:investmentEntityId/:entityType/notification-recipients/view`,
		alias: [`/account/investment-accounts/:investmentEntityId/:entityType/notification-recipients/manage`],
		name: 'settings-email-recipients-view',
		beforeEnter: async (to, from, next) => {
			await investmentEntity.storeEmailRecipients();
			next();
		},
		component: () =>
			import(
				/* webpackChunkName: "settings-email-recipients-view" */ '@views/settings/settings-email-recipients-view.vue'
			)
	},
	{
		path: `/account/investment-accounts/:investmentEntityId/:entityType/notification-recipients/add`,
		name: 'settings-email-recipients-new',
		beforeEnter: async (to, from, next) => {
			await investmentEntity.storeEmailRecipients();
			next();
		},
		component: () =>
			import(
				/* webpackChunkName: "settings-email-recipients-form" */ '@views/settings/settings-email-recipients-form.vue'
			)
	},
	{
		path: `/account/investment-accounts/:investmentEntityId/:entityType/notification-recipients/:recipientId/edit`,
		name: 'settings-email-recipients-edit',
		beforeEnter: async (to, from, next) => {
			await investmentEntity.storeEmailRecipients();
			next();
		},
		component: () =>
			import(
				/* webpackChunkName: "settings-email-recipients-form" */ '@views/settings/settings-email-recipients-form.vue'
			)
	},
	{
		path: `/account/investment-accounts/:investmentEntityId/:entityType/document-delivery/edit`,
		alias: [
			`/account/investment-accounts/:investmentEntityId/:entityType/document-delivery/manage`,
			`/account/investment-accounts/:entityType/document-delivery/edit`,
			`/account/investment-accounts/:entityType/document-delivery/manage`
		],
		name: 'settings-document-delivery-edit',
		beforeEnter: async (to, from, next) => {
			await investmentEntity.storeDocumentDeliveryPreference();
			next();
		},
		component: () =>
			import(
				/* webpackChunkName: "settings-document-delivery-edit" */ '@views/settings/settings-document-delivery-edit.vue'
			)
	}
];

export default settingsRoutes;
